import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";
import { CACHE_VERSION } from "../lib/cacheVersion";
import { toOrgDateKey } from "../lib/timezone";
import { dialCacheTtl, DIAL_CLEANUP_THRESHOLD } from "../lib/dialCacheTtl";

// =============================================================================
// useDialerScorecards — "Dialer Performance" tab data
// =============================================================================
// PURPOSE: Per-dialer scorecards against the course's dialer KPIs.
//
// ARCHITECTURE (cloned from useDMAnalytics): calls RPC `get_dialer_scorecards`
// (returns a json ARRAY, one element per dialer), stale-while-revalidate cache,
// 15-min live / 24h historical TTL, AbortController cleanup.
// =============================================================================

const CACHE_PREFIX = `altara_${CACHE_VERSION}_dial_scorecards_`;

export interface DialerHourlyBucket {
  hour: number; // 0-23, org-tz
  dials: number;
}

export interface DialerScorecard {
  dialerId: string;
  fullName: string;
  ghlUserId: string | null;
  role: string | null; // organization_members.role — grid groups Dialers vs Closers
  dials: number;
  attempts: number;
  rawDials: number;
  unknown: number; // RPC does not emit this until R6 migration; reads 0 until then
  activeDays: number;
  dialsPerActiveDay: number;
  connected: number; // raw completed+answered (pickups) — not shown as a rate
  realConnects: number; // completed+answered AND duration>=45s. Powers connectRate.
  connectRate: number; // 0-1 fraction — real_connects / dials
  conversations: number;
  conversationRate: number; // 0-1 fraction — conversations / real_connects
  leadsTouched: number;
  attemptsPerLead: number;
  booked: number; // plain count; Connect-to-Book RATE dropped 2026-07-04 (Mako Q14)
  recordingsStored: number;
  hourly: DialerHourlyBucket[]; // sparse — hours with 0 dials omitted
}

interface CacheEntry {
  data: DialerScorecard[];
  timestamp: number;
}

const getCacheKey = (orgId: string, start: Date | null, end: Date | null): string => {
  const startStr = start ? toOrgDateKey(start) : "all";
  const endStr = end ? toOrgDateKey(end) : "all";
  return `${CACHE_PREFIX}${orgId}_${startStr}_${endStr}`;
};

const getCache = (
  orgId: string,
  start: Date | null,
  end: Date | null
): DialerScorecard[] | null => {
  try {
    const raw = localStorage.getItem(getCacheKey(orgId, start, end));
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    const ttl = dialCacheTtl(end);
    if (Date.now() - entry.timestamp > ttl) {
      localStorage.removeItem(getCacheKey(orgId, start, end));
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
};

const getStaleCache = (
  orgId: string,
  start: Date | null,
  end: Date | null
): DialerScorecard[] | null => {
  try {
    const raw = localStorage.getItem(getCacheKey(orgId, start, end));
    if (!raw) return null;
    return (JSON.parse(raw) as CacheEntry).data;
  } catch {
    return null;
  }
};

const setCache = (
  orgId: string,
  start: Date | null,
  end: Date | null,
  data: DialerScorecard[]
): void => {
  try {
    localStorage.setItem(
      getCacheKey(orgId, start, end),
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch {
    logger.warn("Dialer scorecards cache write failed");
  }
};

const cleanupOldCache = (): void => {
  try {
    const now = Date.now();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        try {
          const entry: CacheEntry = JSON.parse(localStorage.getItem(key)!);
          if (now - entry.timestamp > DIAL_CLEANUP_THRESHOLD) localStorage.removeItem(key);
        } catch {
          localStorage.removeItem(key!);
        }
      }
    }
  } catch {
    // ignore cleanup errors
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parse = (rows: any[]): DialerScorecard[] =>
  (rows || []).map((r) => ({
    dialerId: r.dialer_id,
    fullName: r.full_name || "Unknown",
    ghlUserId: r.ghl_user_id ?? null,
    role: r.role ?? null,
    dials: r.dials || 0,
    attempts: r.attempts || 0,
    rawDials: r.raw_dials || 0,
    unknown: r.unknown ?? 0,
    activeDays: r.active_days || 0,
    dialsPerActiveDay: r.dials_per_active_day || 0,
    connected: r.connected || 0,
    realConnects: r.real_connects || 0,
    connectRate: r.connect_rate || 0,
    conversations: r.conversations || 0,
    conversationRate: r.conversation_rate || 0,
    leadsTouched: r.leads_touched || 0,
    attemptsPerLead: r.attempts_per_lead || 0,
    booked: r.booked || 0,
    recordingsStored: r.recordings_stored || 0,
    hourly: Array.isArray(r.hourly) ? r.hourly : [],
  }));

export const useDialerScorecards = (
  organizationId: string | null,
  dateRange: { start: Date | null; end: Date | null }
) => {
  const [data, setData] = useState<DialerScorecard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasCleanedUp = useRef(false);

  useEffect(() => {
    if (!hasCleanedUp.current) {
      cleanupOldCache();
      hasCleanedUp.current = true;
    }
  }, []);

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      if (!supabase || !organizationId) {
        setData([]);
        return;
      }

      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
      const controller = abortControllerRef.current;

      const startDate = dateRange.start ? new Date(dateRange.start) : null;
      const endDate = dateRange.end ? new Date(dateRange.end) : null;

      if (!forceRefresh) {
        const cached = getCache(organizationId, startDate, endDate);
        if (cached) {
          setData(cached);
        } else {
          const stale = getStaleCache(organizationId, startDate, endDate);
          if (stale) setData(stale);
          else setLoading(true);
        }
      } else {
        setLoading(true);
      }

      setError(null);

      try {
        if (endDate) endDate.setHours(23, 59, 59, 999);

        const { data: result, error: rpcError } = await supabase
          .rpc("get_dialer_scorecards", {
            p_organization_id: organizationId,
            p_start_date: startDate?.toISOString() || null,
            p_end_date: endDate?.toISOString() || null,
          })
          .abortSignal(controller.signal);

        if (rpcError) {
          if (controller.signal.aborted) return;
          logger.error("Dialer scorecards RPC error:", rpcError);
          setError(rpcError.message);
          return;
        }

        if (result) {
          const newData = parse(result);
          setData(newData);
          setCache(organizationId, startDate, endDate, newData);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        if ((err as Error).name === "AbortError") return;
        logger.error("Dialer scorecards fetch error:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [organizationId, dateRange.start, dateRange.end]
  );

  useEffect(() => {
    fetchData();
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchData]);

  return { data, loading, error };
};
